import { useMemo, useRef } from 'react'

import {
  CustomFieldRenderProps,
  F0FormSubmitResult,
  F0WizardFormPerSectionSubmitArg,
  F0WizardFormStep,
  f0FormField,
  useF0FormDefinition,
} from '@factorialco/f0-react'
import { NumberInput } from '@factorialco/f0-react/dist/experimental'
import { z } from 'zod'

import i18n from 'modules/core/lib/i18n'

import { useSingleLegalEntitySelectorFormField } from 'modules/core/lib/formFields/legalEntitySelector'

import AllocationField from './AllocationField'

// -- Schema builders --

function buildBasicDetailsSchema(
  legalEntityId: ReturnType<typeof useSingleLegalEntitySelectorFormField>,
) {
  return z.object({
    legalEntityId,
    name: f0FormField(z.string().min(1), {
      label: i18n.t('budgets.forms.new_budget.basic_information.budget_title'),
      placeholder: i18n.t('budgets.forms.new_budget.basic_information.budget_title_placeholder'),
      fieldType: 'text',
    }),
    description: f0FormField(z.string().optional(), {
      label: i18n.t('budgets.forms.new_budget.basic_information.budget_description'),
      placeholder: i18n.t('budgets.forms.new_budget.basic_information.budget_description_placeholder'),
      fieldType: 'textarea',
    }),
  })
}

function buildAmountsAndDatesSchema(currencyRef: React.MutableRefObject<string>) {
  return z
    .object({
      amountCents: f0FormField(z.number().min(0.01, i18n.t('budgets.forms.errors.invalid_amount')), {
        label: i18n.t('budgets.forms.new_budget.budget_amounts.amount.title'),
        fieldType: 'custom',
        render: ({ value, onChange, disabled, error }: CustomFieldRenderProps<number>) => (
          <NumberInput
            required
            label={i18n.t('budgets.forms.new_budget.budget_amounts.amount.title')}
            locale={i18n.locale}
            units={currencyRef.current}
            value={value ?? null}
            onChange={(v: number | null) => onChange(v ?? 0)}
            disabled={disabled}
            error={error}
            maxDecimals={2}
            size="md"
          />
        ),
      }),
      effectiveFrom: f0FormField(z.date(), {
        label: i18n.t('budgets.forms.new_budget.budget_limits.start_date'),
        placeholder: i18n.t('budgets.forms.new_budget.budget_limits.start_date_placeholder'),
        fieldType: 'date',
        row: 'dates',
      }),
      effectiveUntil: f0FormField(z.date().optional(), {
        label: i18n.t('budgets.forms.new_budget.budget_limits.end_date'),
        placeholder: i18n.t('budgets.forms.new_budget.budget_limits.end_date_placeholder'),
        fieldType: 'date',
        row: 'dates',
      }),
    })
    .superRefine((data, ctx) => {
      if (
        data.effectiveUntil &&
        data.effectiveFrom &&
        data.effectiveFrom > data.effectiveUntil
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('budgets.forms.errors.start_date'),
          path: ['effectiveFrom'],
        })
      }
    })
}

function buildAllocationSchema(
  currencyRef: React.MutableRefObject<string>,
  budgetAmountRef: React.RefObject<number>,
) {
  return z.object({
    allocationField: f0FormField(
      z.object({
        selectedTrainingClassIds: z.array(z.string()),
        selectedTrainingIds: z.array(z.string()),
      }),
      {
        label: '',
        fieldType: 'custom',
        render: ({
          value,
          onChange,
        }: CustomFieldRenderProps<{
          selectedTrainingClassIds: string[]
          selectedTrainingIds: string[]
        }>) => (
          <AllocationField
            value={value}
            onChange={onChange}
            currency={currencyRef.current}
            budgetAmountCents={budgetAmountRef.current ?? 0}
          />
        ),
      },
    ),
  })
}

// -- Exported types --

function buildTrainingsBudgetSchema(params: {
  legalEntityId: ReturnType<typeof useSingleLegalEntitySelectorFormField>
  currencyRef: React.MutableRefObject<string>
  budgetAmountRef: React.RefObject<number>
}) {
  return {
    basicDetails: buildBasicDetailsSchema(params.legalEntityId),
    amountsAndDates: buildAmountsAndDatesSchema(params.currencyRef),
    allocation: buildAllocationSchema(params.currencyRef, params.budgetAmountRef),
  }
}

export type TrainingsBudgetSchema = ReturnType<typeof buildTrainingsBudgetSchema>

export type TrainingsBudgetFormValues = {
  [K in keyof TrainingsBudgetSchema]: z.infer<TrainingsBudgetSchema[K]>
}

// -- Hook --

export interface UseTrainingsBudgetFormParams {
  defaultLegalEntityId: string | undefined
  currency: string
  currencyMap: Record<string, string>
  onSubmit: (
    arg: F0WizardFormPerSectionSubmitArg<TrainingsBudgetSchema>,
  ) => Promise<F0FormSubmitResult>
}

export const useTrainingsBudgetForm = ({
  defaultLegalEntityId,
  currency,
  currencyMap,
  onSubmit,
}: UseTrainingsBudgetFormParams) => {
  const legalEntityId = useSingleLegalEntitySelectorFormField({
    mandatory: true,
    label: i18n.t('budgets.forms.new_budget.basic_information.legal_entity'),
    section: '',
  })

  const currencyRef = useRef<string>(currency)
  const budgetAmountRef = useRef<number>(0)

  // Wrap onSubmit to capture currency on basicDetails and amountCents on amountsAndDates
  const wrappedOnSubmit = useMemo(
    () =>
      async (arg: F0WizardFormPerSectionSubmitArg<TrainingsBudgetSchema>) => {
        if (arg.sectionId === 'basicDetails') {
          const selectedId = arg.data.legalEntityId
          if (selectedId && currencyMap[selectedId]) {
            currencyRef.current = currencyMap[selectedId]
          }
        }
        if (arg.sectionId === 'amountsAndDates') {
          budgetAmountRef.current = arg.data.amountCents
        }
        return onSubmit(arg)
      },
    [onSubmit, currencyMap],
  )

  const schema = useMemo(
    () => buildTrainingsBudgetSchema({ legalEntityId, currencyRef, budgetAmountRef }),
    [legalEntityId, currencyRef, budgetAmountRef],
  )

  const defaultValues = useMemo(
    () => ({
      basicDetails: {
        legalEntityId: defaultLegalEntityId,
        name: '',
        description: undefined,
      },
      amountsAndDates: {
        amountCents: undefined,
        effectiveFrom: undefined,
        effectiveUntil: undefined,
      },
      allocation: {
        allocationField: {
          selectedTrainingClassIds: new Array<string>(),
          selectedTrainingIds: new Array<string>(),
        },
      },
    }),
    [defaultLegalEntityId],
  )

  const sections = useMemo(
    () => ({
      basicDetails: {
        title: i18n.t('budgets.forms.trainings_budget.basic_details.title'),
        description: i18n.t('budgets.forms.trainings_budget.basic_details.description'),
      },
      amountsAndDates: {
        title: i18n.t('budgets.forms.trainings_budget.amounts_and_dates.title'),
        description: i18n.t('budgets.forms.trainings_budget.amounts_and_dates.description'),
      },
      allocation: {
        title: i18n.t('budgets.forms.trainings_budget.allocation.title'),
        description: i18n.t('budgets.forms.trainings_budget.allocation.description'),
      },
    }),
    [],
  )

  const steps: F0WizardFormStep[] = useMemo(
    () => [
      {
        title: i18n.t('budgets.forms.trainings_budget.basic_details.title'),
        sectionIds: ['basicDetails'],
      },
      {
        title: i18n.t('budgets.forms.trainings_budget.amounts_and_dates.title'),
        sectionIds: ['amountsAndDates'],
      },
      {
        title: i18n.t('budgets.forms.trainings_budget.allocation.title'),
        sectionIds: ['allocation'],
        nextLabel: i18n.t('common.submit'),
      },
    ],
    [],
  )

  const formDefinition = useF0FormDefinition({
    name: 'new-trainings-budget',
    schema,
    sections,
    defaultValues,
    onSubmit: wrappedOnSubmit,
  })

  return { formDefinition, steps }
}
